package com.ricky.realtimeledger.theme

import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.silk.init.InitSilk
import com.varabyte.kobweb.silk.init.InitSilkContext
import com.varabyte.kobweb.silk.theme.colors.ColorMode
import com.varabyte.kobweb.silk.theme.colors.palette.background
import com.varabyte.kobweb.silk.theme.colors.palette.color

/**
 * @property nearBackground A useful color to apply to a container that should differentiate itself from the background
 * but just a little. Updated to use the champagne/cream tone from the logo palette.
 */
class SitePalette(
    val nearBackground: Color,
    val cobweb: Color,
    val brand: Brand,
    val custom: Custom
) {
    class Brand(
        // --- 10% ACCENT COLORS ---
        // Reserved strictly for high-value interactions (CTAs, Links, Icons, Active Tabs)
        val primary: Color = Color.rgb(0x976659),       // Color 1: Deep Rose/Bronze
        val primaryHover: Color = Color.rgb(0xb77d66),  // Color 2: Muted Terracotta
        val accent: Color = Color.rgb(0xd9a578),        // Color 3: Warm Metallic Gold

        // --- 30% SECONDARY COLORS ---
        // Used for alternating section backgrounds, cards, or borders
        val primarySoft: Color = Color.rgb(0xf2c89c),   // Color 4: Soft Peach
        val neutralLight: Color = Color.rgb(0xf2e1ba)   // Color 5: Champagne Ivory (New)
    )

    class Custom(
        // --- 30% SECONDARY COLORS (Typography) ---
        // Creates sharp readability without the harsh contrast of pure black
        val darkText: Color = Color.rgb(0x323232),

        // --- 60% DOMINANT COLORS ---
        // Used for the vast majority of the site's negative space and base backgrounds
        val lightGray: Color = Color.rgb(0xF8F9FA), // Clean, modern off-white
        val white: Color = Colors.White,

        // UI Utility
        val divider: Color = Color.rgb(0xE2E8F0) // Subtle, structural borders
    )
}

object SitePalettes {
    val light = SitePalette(
        // Using Color 5 here brings the warm, premium logo aesthetic into the page containers
        nearBackground = Color.rgb(0xf2e1ba),
        cobweb = Colors.LightGray,
        brand = SitePalette.Brand(),
        custom = SitePalette.Custom()
    )
}

fun ColorMode.toSitePalette(): SitePalette {
    return SitePalettes.light
}

@InitSilk
fun initTheme(ctx: InitSilkContext) {
    // TELL KOBWEB TO NEVER LISTEN TO THE OS DARK MODE PREFERENCE
    ctx.config.initialColorMode = ColorMode.LIGHT

    // Set the 60% and 30% global defaults at the absolute root level
    ctx.theme.palettes.light.background = Colors.White
    ctx.theme.palettes.light.color = Color.rgb(0x323232)

    ctx.theme.palettes.dark.background = Color.rgb(0x06080B)
    ctx.theme.palettes.dark.color = Colors.White
}